import React, { useMemo } from 'react';
import { WithCopy } from '@scale/frontend-lib/sensor-fusion';
import { Typography } from '@scale/scaleui';
import { ISensorFusionUnifiedProjectLookupDto } from '@scale/sensor-fusion-shared';
import { LookupIdLink } from 'react-scale/dashboard.scale.com/components/SensorFusionLookup/common';

import { useRemoAdminCtx } from 'react-scale/components/remoAdmin/remoAdminContext';
import { CORP_LOOKUP_PATH, REMOADMIN_LOOKUP_PATH } from '../constants';
import { useSensorFusionLookupContext } from '../contexts/SensorFusionLookupContext';

type SfuOverviewPanelProps = {
  project: ISensorFusionUnifiedProjectLookupDto;
};

const SfuOverviewPanel = ({ project }: SfuOverviewPanelProps) => {
  const { customer, createdAt, permissionGroup, taxonomy } = project;

  const { isRemoAdmin } = useRemoAdminCtx();
  const { scaleDashboardFrontendUrl, remotasksFrontendUrl } = useSensorFusionLookupContext() ?? {};

  const toolUrl = useMemo(() => {
    if (isRemoAdmin) {
      return `${remotasksFrontendUrl ?? ''}/${REMOADMIN_LOOKUP_PATH}`;
    }
    return `${scaleDashboardFrontendUrl ?? ''}/${CORP_LOOKUP_PATH}`;
  }, [isRemoAdmin, remotasksFrontendUrl, scaleDashboardFrontendUrl]);

  return (
    <div className="border border-solid border-neutral-200 px-4 py-4 flex flex-col gap-2">
      <Typography.H3>Project Details</Typography.H3>
      <div className="flex flex-col">
        <span className="font-medium">{project.name}</span>

        {!isRemoAdmin && (
          <div>
            <span className="font-medium">Customer: </span>
            {customer.company != null && `${customer.company} `}
            <WithCopy className="inline-block text-info-700" copyText={customer.id}>
              <a href={`${toolUrl}/${customer.id}`}>{customer.id}</a>
            </WithCopy>
          </div>
        )}
        {!isRemoAdmin && (
          <div className="flex flex-col ml-2">
            {customer.email != null && (
              <WithCopy copyText={customer.email}>Email: {customer.email}</WithCopy>
            )}
          </div>
        )}
      </div>
      <div>
        <span className="font-medium">Permission Group: </span>
        {`${permissionGroup.name}`}
        <WithCopy className="inline-block text-info-700" copyText={permissionGroup.id}>
          <a href={`${toolUrl}/${permissionGroup.id}`}>{permissionGroup.id}</a>
        </WithCopy>
      </div>
      {taxonomy && !isRemoAdmin && (
        <div>
          <span className="font-medium">Taxonomy: </span>
          <LookupIdLink className="inline-block" id={taxonomy.srn ?? taxonomy.id} />
        </div>
      )}
      <div>
        <span className="font-medium">Created At: </span>
        {new Date(createdAt).toLocaleString()}
      </div>
    </div>
  );
};

export default SfuOverviewPanel;
